/**
 * "Help me write" for the Candidate Requests reply editor: the state behind the ✨ Rewrite bar.
 *
 * Its own hook rather than more state in InboundCard, which is at its 500-line ceiling — and
 * because everything here is about one question (what happens between "Enter" and new text in the
 * editor) that is worth reading in one place.
 *
 * Two rules it exists to enforce:
 *
 *  1. **A refusal must never cost the recruiter their words.** The editor is written to ONLY on a
 *     successful rewrite. A policy refusal, a model outage or a transport error leaves the draft
 *     exactly as it was, and says so.
 *  2. **Every rewrite is undoable.** One level: the previous HTML is kept before the new text goes
 *     out. Deeper history would be a text editor's job; one step covers the actual mistake, which is
 *     asking for the wrong thing and wanting the draft back. It is STATE rather than a ref because
 *     whether Undo renders depends on it, and reading a ref during render is exactly what
 *     react-hooks forbids (it caught this).
 */
import { useState, type RefObject } from 'react';
import * as api from '../../services/taActionBoardApi';
import { toEditableHtml } from './format';
import { logAnalyticsEvent } from '../../../../analytics/nativeAnalytics';
import type { InboundRequest } from '../../types/taInboundRequests';
import type { RefineProps } from './EmailBodyEditor';

/**
 * Owns how a rewrite reaches the editor, so the card does not have to.
 *
 * Reads the live contentEditable rather than React state, because the recruiter's own typing only
 * exists in the DOM — rewriting `item.draft` would silently throw their edits away. `innerText`
 * gives plain text with `<br>` already collapsed to newlines, which is exactly what the model wants
 * back; `innerHTML` is what an undo has to restore.
 */
export function useReplyRefine({
  item,
  german,
  editorRef,
  /** What the editor shows before any edit — the fallback when the ref is not mounted yet. */
  draftHtml,
  setEditorHtml,
  /**
   * Clears the slot mirror's provenance (`lastWrittenProposal` in replyProposal.tsx). Called on
   * every rewrite: it is the block the mirror remembers writing, and after a rewrite that text is
   * gone from the editor — left set, the mirror's "untouched" check compares against text it never
   * wrote and freezes silently. A callback rather than the ref itself, because the ref belongs to
   * the card and a hook may not mutate what it was handed.
   */
  clearProposal,
  setTouched,
}: {
  item: InboundRequest;
  german: boolean;
  editorRef: RefObject<HTMLDivElement | null>;
  draftHtml: string;
  setEditorHtml: (html: string) => void;
  clearProposal: () => void;
  setTouched: (touched: boolean) => void;
}): RefineProps {
  /**
   * The editor as text — with every link kept as `[label](href)`, the one markup the rewrite
   * understands (`refinedToHtml` on the proxy turns it back into an anchor). `innerText` alone
   * would hand the model "career site" and drop the URL, so a second rewrite would lose the link.
   */
  const readText = () => {
    const editor = editorRef.current;
    if (!editor) return item.draft ?? '';
    const decode = (html: string) => {
      const d = document.createElement('div');
      d.innerHTML = html;
      return d.textContent || '';
    };
    // Text that goes back INTO the scratch HTML must be re-escaped, or a "<" in a label is parsed.
    const escape = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const withLinks = editor.innerHTML.replace(
      /<a\b[^>]*?href="([^"]*)"[^>]*>([\s\S]*?)<\/a>/gi,
      (_m, rawHref: string, inner: string) => {
        // Both sides DECODED before comparing: innerHTML serialises `&` in an href as `&amp;`.
        const href = decode(rawHref);
        const label = decode(inner) || href;
        // A bare-URL link reads back as the URL itself; a labelled one as markdown.
        return escape(label === href ? href : `[${label}](${href})`);
      }
    );
    // `innerText` needs layout, so the transformed HTML is read through an attached scratch node —
    // the same line-collapsing the live editor's innerText gives, links preserved.
    const scratch = document.createElement('div');
    scratch.style.position = 'absolute';
    scratch.style.left = '-9999px';
    scratch.innerHTML = withLinks;
    document.body.appendChild(scratch);
    const text = scratch.innerText;
    scratch.remove();
    return text;
  };
  const readHtml = () => editorRef.current?.innerHTML ?? draftHtml;
  const writeHtml = (html: string) => {
    setEditorHtml(html);
    clearProposal();
    setTouched(true);
  };
  const writeText = (text: string) => writeHtml(toEditableHtml(text));

  const [busy, setBusy] = useState(false);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [previousHtml, setPreviousHtml] = useState<string | null>(null);

  const onRefine = async (instruction: string): Promise<boolean> => {
    setBusy(true);
    setRefusal(null);
    setWarnings([]);
    /** What happened, for the one event in `finally`: applied / refused / failed — one count was three outcomes. */
    let outcome: 'applied' | 'refused' | 'failed' = 'failed';
    let warningCount = 0;
    // Snapshot BEFORE the call, and keep it even on failure: the draft is unchanged either way, so
    // an undo offered after a refusal is harmless, while losing the snapshot on a retry is not.
    const snapshot = readHtml();
    try {
      const result = await api.refineRequestReply({
        threadId: item.threadId,
        body: readText(),
        instruction,
        /**
         * `questionFull`, not `question`: the card truncates for display, and rewriting against a
         * question that cuts off mid-sentence produces a reply to half of it.
         */
        question: item.questionFull || item.question || '',
        category: item.category,
        language: german ? 'de' : 'en',
        candidateFirstName: (item.candidateName || '').trim().split(/\s+/)[0] || '',
        /**
         * What the card shows above the editor — the interviewers' note and the decision log's AGG
         * paragraph — so "add more feedback" has something to add from (Sofia, Sep 10 2026).
         */
        context: {
          /**
           * The raw interviewer note only where the reply IS a feedback reply — every Lever card now
           * carries the note, and a status or scheduling rewrite has no business drawing on interview
           * assessments (that path bypasses the AGG generator's own enforcement loop). The approved AGG
           * paragraph is safe to offer everywhere: it was already sent to this candidate.
           */
          feedbackNote:
            item.draftSource === 'feedback' || item.category === 'feedback_request'
              ? item.feedbackNote?.text || undefined
              : undefined,
          decisionFeedback: item.decisionFeedback?.prose || undefined,
        },
      });

      if (!result.ok || !result.text) {
        outcome = 'refused';
        setRefusal(result.refusal || 'The rewrite could not be applied, so your text is unchanged.');
        return false;
      }
      outcome = 'applied';
      warningCount = result.warnings?.length ?? 0;
      setPreviousHtml(snapshot);
      // The proxy's HTML carries the one markup it converts ([phrase](url) → <a>); older proxies send text only.
      if (result.html) writeHtml(result.html);
      else writeText(result.text);
      setWarnings(result.warnings || []);
      return true;
    } catch (e) {
      setRefusal(
        e instanceof Error ? `${e.message} Your text is unchanged.` : 'The rewrite failed. Your text is unchanged.'
      );
      return false;
    } finally {
      setBusy(false);
      /**
       * Categories and counts only — never the instruction, the draft or the candidate. A recruiter
       * types things like "tell Marta we cannot do Thursday" into that box, so the text itself is
       * exactly what the PII rule keeps out of every sink (see CLAUDE.md). The LENGTH is safe and is
       * the useful signal: it says whether people write two words or two sentences.
       */
      void logAnalyticsEvent('ta_requests_reply_refined', {
        category: item.category,
        draftSource: item.draftSource,
        instruction_length: instruction.length,
        outcome,
        warning_count: warningCount,
      });
    }
  };

  const onUndo =
    previousHtml === null
      ? undefined
      : () => {
          // The undo rate is the quality signal for the feature.
          void logAnalyticsEvent('ta_requests_refine_undone', { category: item.category, draftSource: item.draftSource });
          writeHtml(previousHtml);
          setPreviousHtml(null);
          setWarnings([]);
          setRefusal(null);
        };

  return { onRefine, busy, warnings, refusal, onUndo };
}
